import React, { useContext, useEffect } from "react";

import { FilePreviewItem } from "./FilePreviewItem";
import { ResultFile, ValidatorFileContext } from "../../hooks/useFile";
import { cn } from "../../utils/style";

type FileData = {
  file: ResultFile;
  realIndex: number;
};

type GroupedFiles = {
  custom: FileData[];
  samples: FileData[];
};

const groupFiles = (files: ResultFile[]): GroupedFiles => {
  return files.reduce(
    (acc, file, index) => {
      const isCustom =
        !file.documentInfo?.industryType ||
        file.documentInfo.industryType === "custom";

      const group = isCustom ? "custom" : "samples";
      acc[group].push({ file, realIndex: index });
      return acc;
    },
    { custom: [], samples: [] } as GroupedFiles,
  );
};

// 썸네일 URL 생성 헬퍼 함수
const getThumbnailSrc = (file: ResultFile): string => {
  const base64Image = file.result.imageBase64?.[0];
  return base64Image ? `data:image/jpeg;base64,${base64Image}` : "";
};

// 커스텀 파일 그룹 컴포넌트
const CustomFilesGroup = ({
  files,
  selectedFileIndex,
  onFileSelect,
  className,
}: {
  files: FileData[];
  selectedFileIndex: number;
  onFileSelect: (index: number) => void;
  className?: string;
}) => (
  <div className={cn("flex gap-2 rounded-e-lg bg-muted p-1.5", className)}>
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        {[...files].reverse().map(({ file, realIndex }) => (
          <FilePreviewItem
            key={file.id}
            fileName={file.title}
            thumbnailSrc={getThumbnailSrc(file)}
            isSelected={realIndex === selectedFileIndex}
            onClick={() => onFileSelect(realIndex)}
          />
        ))}
      </div>
    </div>
  </div>
);

// 프리뷰 포함된 파일 리스트 컴포넌트
export const CustomFilePreviewList = () => {
  const { files, selectedFileIndex, setSelectedFileIndex } =
    useContext(ValidatorFileContext);

  const { custom: customFiles } = groupFiles(files);

  useEffect(() => {
    if (selectedFileIndex !== -1) return;

    if (customFiles.length > 0) {
      setSelectedFileIndex(customFiles[0].realIndex);
      return;
    }
  }, [selectedFileIndex, customFiles, setSelectedFileIndex]);

  // 파일이 없으면 렌더링 하지 않음
  if (files.length === 0) {
    return null;
  }

  return (
    <div className={cn("mb-2.5 flex items-center justify-start self-end")}>
      {customFiles.length > 0 && (
        <CustomFilesGroup
          files={customFiles}
          selectedFileIndex={selectedFileIndex}
          onFileSelect={realIndex => {
            setSelectedFileIndex(realIndex);
          }}
        />
      )}
    </div>
  );
};
